import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import * as Pages from '$pages';
import { FixedTitle } from '$partials/layout/side-titles.jsx';
import { LanguageProvider } from '$contexts/language.jsx';
import { setUserAgent } from '$utilities/user-agent';
import { SkipLink } from '$partials/ui';
import { SideTitlesProvider } from '$contexts/side-titles.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 *
 * Renders the application.
 * 1. If the page component requested doesn’t exist, escapes the function.
 * 2. Skip link are useful for keyboard users to access the main content of the page
 *    straight away without the hassle of navigating through the navigation at each page load.
 * 3. Page component (UnionPage, etc.).
 */
const App = (props) => {
    // hooks
    useEffect(() => {
        if (props.userAgent) {
            setUserAgent(props.userAgent);
        }
    }, [ props.userAgent ]);

    // misc
    /* [1] */
    if (!props.pageComponent || typeof(Pages[props.pageComponent]) === 'undefined') {
        return null;
    }

    const Component = Pages[props.pageComponent];

    // render
    return (
        <LanguageProvider
            current={props.language?.current || 'en'}
            translations={props.data?.translations?.terms || []}>
            <SideTitlesProvider>
                <SkipLink href='#main-content'>Skip to main content</SkipLink> {/* [2] */}
                <Component {...props} /> {/* [3] */}
                <FixedTitle />
            </SideTitlesProvider>
        </LanguageProvider>
    );
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

const propTypes = {
    data         : PropTypes.object.isRequired,
    dev          : PropTypes.bool,
    domain       : PropTypes.string,
    language     : PropTypes.object.isRequired,
    pageComponent: PropTypes.string.isRequired,
    timezone     : PropTypes.string,
    userAgent    : PropTypes.object.isRequired
};

App.propTypes = propTypes;

export { App };

export default App;
